import styled, { css, keyframes } from "styled-components/macro";
import { rem } from "polished";

import { BREAKPOINT_DESKTOP, BREAKPOINT_TABLET } from "../../constants";
import { colors } from "../Styles.variables";
import Card, { CardContent } from "./Card";

const CardSkeleton = ({ targetDevice, theme = "light" }) => {
  return (
    <StyledCard theme={theme}>
      <CardPhotoSkeleton theme={theme} />
      <CardContent>
        <CardTitleSkeleton theme={theme} />
        <CardTitleSkeleton theme={theme} />
        <CardContentSkeleton theme={theme} />
        {targetDevice === "mobile" ? (
          <CardActionSkeleton>
            <ActionSkeleton theme={theme} />
          </CardActionSkeleton>
        ) : (
          <CardActionSkeleton>
            <ActionSkeleton width={175} theme={theme} />
            <ActionSkeleton width={98} theme={theme} />
          </CardActionSkeleton>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default CardSkeleton;

export const shimmer = keyframes`
  100% {
    transform: translateX(100%);
  }
`;

export const lightGradient = css`
  background: linear-gradient(to right, #dfdfdf 0%, #f3f3f3 50%, #dfdfdf 100%);
`;

export const darkGradient = css`
  background: linear-gradient(
    to right,
    #3d3d3d 0%,
    #3f3f3f 10%,
    #4a4a4a 20%,
    #3f3f3f 30%,
    #3a3a3a 50%,
    #3d3d3d 100%
  );
`;

const StyledCard = styled(Card)`
  background-color: ${props =>
    props.theme === "dark" ? "#2f2e2e" : colors.white};
  border-radius: 0;
  margin: ${rem("9px")} 0;

  @media ${BREAKPOINT_TABLET} {
    border-radius: 10px;
    margin: 0;
  }
`;

const CardPhotoSkeleton = styled.div`
  background-color: ${({ theme }) =>
    theme === "dark" ? "#3d3d3d" : "#dfdfdf"};
  border-radius: 0;
  height: 0;
  overflow: hidden;
  /* To keep aspect ratio of images on mobile */
  padding-top: calc(275 / 480 * 100%);
  position: relative;

  &:after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    ${({ theme }) => (theme === "dark" ? darkGradient : lightGradient)}
    animation: ${shimmer} 2s ease-in-out infinite;
    content: "";
  }

  @media ${BREAKPOINT_TABLET} {
    border-radius: 10px 10px 0 0;
    /* To keep aspect ratio of images on tablet */
    padding-top: calc(217 / 455 * 100%);
  }

  @media ${BREAKPOINT_DESKTOP} {
    /* To keep aspect ratio of images on desktop */
    padding-top: calc(232 / 410 * 100%);
  }
`;

const CardTitleSkeleton = styled.div`
  background-color: ${({ theme }) =>
    theme === "dark" ? "#3d3d3d" : "#dfdfdf"};
  height: 20px;
  margin-bottom: 9px;
  overflow: hidden;
  position: relative;
  width: 100%;

  &:after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    ${({ theme }) => (theme === "dark" ? darkGradient : lightGradient)}
    animation: 2s ${shimmer} 0.1s ease-in-out infinite;
    content: "";
  }
`;

const CardContentSkeleton = styled.div`
  background-color: ${({ theme }) =>
    theme === "dark" ? "#3d3d3d" : "#dfdfdf"};
  height: 40px;
  margin-top: 26px;
  overflow: hidden;
  position: relative;
  width: 100%;

  &:after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    ${({ theme }) => (theme === "dark" ? darkGradient : lightGradient)}
    animation: 2s ${shimmer} 0.25s ease-in-out infinite;
    content: "";
  }
`;

const CardActionSkeleton = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${rem("20px")};
  margin-top: 26px;
`;

const ActionSkeleton = styled.div`
  background-color: ${({ theme }) =>
    theme === "dark" ? "#3d3d3d" : "#dfdfdf"};
  border-radius: ${rem("8px")};
  height: 40px;
  overflow: hidden;
  position: relative;
  width: ${({ width }) => (!!width ? width + "px" : "100%")};

  @media ${BREAKPOINT_TABLET} {
    border-radius: 0;
  }

  &:after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    ${({ theme }) => (theme === "dark" ? darkGradient : lightGradient)}
    animation: 2s ${shimmer} 0.4s ease-in-out infinite;
    content: "";
  }
`;
